import { useEffect, useState } from 'react';
import { Button, Dialog, RangePicker, type DateRange } from '@forecastify/ui';
import { addWireDays, toWireDate } from '@forecastify/shared';
import { useI18n, useT } from '../../i18n';
import { useCalendarLabels } from '../date-field';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `0199` · a sync over a window somebody chose — the "…" beside *Sync recent*.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE RANGE IS ON THE TRACKER'S CHANGE CLOCK, AND THE DIALOG SAYS SO IN ITS FIRST LINE.**
 * The only feed either provider offers is *what changed since*; an hour worked in June and edited
 * in September is a September change. Somebody choosing *June* to fetch June's hours gets the hours
 * that were last touched in June — which is most of them, and not all. Hiding that would make the
 * backfill look like it lost hours it was never asked for.
 *
 * ⚠️ **Floored at the instance's start date, and the floor is shown rather than silently applied.**
 * The server floors the window either way (`jira.md` §9.4); a preset that reaches below the floor
 * is clamped here so what the person sees is what will run.
 *
 * ⚠️ **Presets are rolling windows ending today**, as asked for: the past week, month and three
 * months, plus *since the start date* — the one the migration needs, and the one that reads
 * furthest back.
 */
export function SyncWindowDialog({
  open,
  sinceOn,
  busy,
  onClose,
  onConfirm,
}: {
  readonly open: boolean;
  /** The instance's start date, a wire date. */
  readonly sinceOn: string;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (range: DateRange) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const calendar = useCalendarLabels();
  const today = toWireDate(new Date());

  const clampFrom = (from: string): string => (from < sinceOn ? sinceOn : from);
  const back = (days: number): DateRange => ({
    from: clampFrom(addWireDays(today, -days) ?? today),
    to: today,
  });
  const monthsBack = (months: number): DateRange => {
    const at = new Date();
    at.setUTCMonth(at.getUTCMonth() - months);
    return { from: clampFrom(toWireDate(at)), to: today };
  };

  const presets = [
    { label: t('integrations.syncWindow.preset.week'), ...back(7) },
    { label: t('integrations.syncWindow.preset.month'), ...monthsBack(1) },
    { label: t('integrations.syncWindow.preset.quarter'), ...monthsBack(3) },
    { label: t('integrations.syncWindow.preset.sinceStart'), from: sinceOn, to: today },
  ];
  const initial = monthsBack(1);
  const [range, setRange] = useState<DateRange>(initial);

  /* Reopening starts from the default again, not from whatever was picked last time. */
  useEffect(() => {
    if (open) setRange(monthsBack(1));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only. */
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('integrations.syncWindow.title')}
      description={t('integrations.syncWindow.description')}
      closeLabel={t('common.close')}
      width={520}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            disabled={busy || range.from > range.to}
            onClick={() => {
              onConfirm({ from: clampFrom(range.from), to: range.to });
            }}
          >
            {t('integrations.syncWindow.confirm')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <RangePicker
          value={range}
          defaultRange={initial}
          onChange={setRange}
          today={today}
          format={(value) => formatWindow(value.from, value.to, locale)}
          presets={presets}
          labels={{
            ...calendar,
            apply: t('range.apply'),
            reset: t('range.reset'),
            trigger: t('range.trigger'),
          }}
        />
        <p className="text-muted-foreground text-[12px] leading-relaxed">
          {t('integrations.syncWindow.floor', { date: sinceOn })}
        </p>
      </div>
    </Dialog>
  );
}

function formatWindow(from: string, to: string, locale: string): string {
  try {
    /* ⚠️ UTC in both the parse and the format — a wire date is a calendar date (`hard-won.md`). */
    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    return formatter.formatRange(new Date(`${from}T00:00:00Z`), new Date(`${to}T00:00:00Z`));
  } catch {
    return `${from} – ${to}`;
  }
}
